import * as React from 'react'
import styled from 'styled-components'
import type { ModelCacheStats } from './modelCache'
import { parseModelUrl } from './urlConfig'

const Screen = styled.div`
  width: 100%;
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(14px, 4vw, 24px);

  @media (max-height: 620px) {
    align-items: flex-start;
  }
`

const Panel = styled.section`
  width: min(620px, 100%);
  max-height: calc(100dvh - 28px);
  padding: clamp(20px, 5vw, 32px);
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.62);
  box-shadow: 0 18px 48px rgba(2, 6, 23, 0.26);
  backdrop-filter: blur(16px);
  overflow: auto;
  overscroll-behavior: contain;
`

const Eyebrow = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(191, 219, 254, 0.64);
`

const Title = styled.h1`
  margin-top: 14px;
  font-size: 2.7rem;
  line-height: 0.98;
  font-weight: 700;
  letter-spacing: 0;
  color: #f8fafc;

  @media (max-width: 560px) {
    font-size: 2rem;
  }
`

const Description = styled.p`
  margin-top: 14px;
  max-width: 32rem;
  font-size: 0.98rem;
  line-height: 1.7;
  color: rgba(226, 232, 240, 0.7);
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 22px;
`

const ActionButton = styled.button<{ $primary?: boolean }>`
  min-width: 152px;
  min-height: 42px;
  padding: 0 16px;
  border: 1px solid ${props => (props.$primary ? 'rgba(148, 163, 184, 0.24)' : 'rgba(148, 163, 184, 0.16)')};
  border-radius: 8px;
  background: ${props => (props.$primary ? 'rgba(30, 41, 59, 0.92)' : 'rgba(15, 23, 42, 0.7)')};
  color: #f8fafc;
  font-size: 0.92rem;
  font-weight: 600;
  transition:
    border-color 180ms ease,
    background 180ms ease,
    transform 180ms ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: rgba(148, 163, 184, 0.3);
    background: rgba(30, 41, 59, 0.96);
  }

  &:focus-visible {
    outline: 2px solid rgba(148, 163, 184, 0.24);
    outline-offset: 2px;
  }

  @media (max-width: 420px) {
    width: 100%;
    min-width: 0;
  }
`

const DemoPicker = styled.div`
  display: grid;
  gap: 8px;
  margin-top: 22px;
`

const RemoteModelForm = styled.form`
  display: grid;
  gap: 10px;
  margin-top: 22px;
`

const CacheNotice = styled.p`
  margin-top: 10px;
  padding-left: 10px;
  border-left: 2px solid rgba(96, 165, 250, 0.36);
  font-size: 0.82rem;
  line-height: 1.55;
  color: rgba(191, 219, 254, 0.76);
`

const CacheStatsLine = styled.span`
  display: block;
  margin-top: 5px;
  color: rgba(226, 232, 240, 0.82);
`

const DemoLabel = styled.label`
  display: grid;
  gap: 7px;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(191, 219, 254, 0.64);
`

const LabelText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
`

const TooltipBubble = styled.span`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  z-index: 4;
  width: min(280px, calc(100vw - 48px));
  padding: 9px 10px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 8px;
  background: rgba(2, 6, 23, 0.96);
  box-shadow: 0 14px 36px rgba(2, 6, 23, 0.32);
  color: rgba(226, 232, 240, 0.88);
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.45;
  opacity: 0;
  pointer-events: none;
  text-transform: none;
  transform: translate(-50%, -4px);
  transition:
    opacity 160ms ease,
    transform 160ms ease,
    visibility 160ms ease;
  visibility: hidden;

  &::before {
    position: absolute;
    top: -5px;
    left: 50%;
    width: 8px;
    height: 8px;
    border-top: 1px solid rgba(148, 163, 184, 0.18);
    border-left: 1px solid rgba(148, 163, 184, 0.18);
    background: rgba(2, 6, 23, 0.96);
    content: '';
    transform: translateX(-50%) rotate(45deg);
  }
`

const Tooltip = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;

  &:hover ${TooltipBubble},
  &:focus-within ${TooltipBubble} {
    opacity: 1;
    transform: translate(-50%, 0);
    visibility: visible;
  }
`

const TooltipTrigger = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1px solid rgba(191, 219, 254, 0.26);
  border-radius: 50%;
  color: rgba(226, 232, 240, 0.82);
  cursor: help;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1;
  text-transform: none;

  &:focus-visible {
    outline: 2px solid rgba(148, 163, 184, 0.24);
    outline-offset: 2px;
  }
`

const UrlInputRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`

const UrlInput = styled.input`
  width: 100%;
  min-height: 42px;
  padding: 0 12px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 8px;
  background: rgba(2, 6, 23, 0.42);
  color: #f8fafc;
  font-size: 0.9rem;

  &::placeholder {
    color: rgba(148, 163, 184, 0.72);
  }

  &:focus-visible {
    outline: 2px solid rgba(148, 163, 184, 0.24);
    outline-offset: 2px;
  }
`

const DemoSelect = styled.select`
  width: 100%;
  min-height: 42px;
  padding: 0 38px 0 12px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 8px;
  background: rgba(2, 6, 23, 0.42);
  color: #f8fafc;
  font-size: 0.92rem;
  font-weight: 600;

  &:focus-visible {
    outline: 2px solid rgba(148, 163, 184, 0.24);
    outline-offset: 2px;
  }
`

const Hint = styled.p`
  margin-top: 14px;
  font-size: 0.86rem;
  line-height: 1.6;
  color: rgba(148, 163, 184, 0.72);
`

const ErrorMessage = styled.p`
  margin-top: 14px;
  padding: 10px 12px;
  border: 1px solid rgba(248, 113, 113, 0.26);
  border-radius: 8px;
  background: rgba(127, 29, 29, 0.24);
  font-size: 0.86rem;
  line-height: 1.55;
  color: rgba(254, 202, 202, 0.9);
`

export type DemoModelOption = {
  id: string
  label: string
  fileName: string
  url: string
}

type Props = {
  demoModels: DemoModelOption[]
  loadError?: string | null
  openDemoModel: (demoModel: DemoModelOption) => void
  openRemoteModelUrl: (modelUrl: string) => Promise<void>
  selectFile?: () => void
  cacheStats?: ModelCacheStats | null
  isCacheStatsLoading?: boolean
  onCacheStatsRequest?: (signal?: AbortSignal) => void
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const

const formatByteSize = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B'
  }

  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1)
  const value = bytes / 1024 ** unitIndex
  const maximumFractionDigits = unitIndex === 0 || value >= 100 ? 0 : value >= 10 ? 1 : 2

  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits }).format(value)} ${BYTE_UNITS[unitIndex]}`
}

const formatCacheStats = (stats: ModelCacheStats) => {
  const itemLabel = stats.itemCount === 1 ? 'item' : 'items'

  return `${stats.itemCount} cached ${itemLabel}, ${formatByteSize(stats.totalSize)} total.`
}

export const StartScreen = (props: Props) => {
  const firstDemoModel = props.demoModels[0]
  const [selectedDemoId, setSelectedDemoId] = React.useState(firstDemoModel?.id ?? '')
  const [remoteModelUrl, setRemoteModelUrl] = React.useState('')
  const [remoteModelError, setRemoteModelError] = React.useState<string | null>(null)
  const [isRemoteModelSubmitting, setRemoteModelSubmitting] = React.useState(false)
  const selectedDemoModel = props.demoModels.find(demoModel => demoModel.id === selectedDemoId) ?? firstDemoModel

  React.useEffect(() => {
    if (!props.onCacheStatsRequest) {
      return
    }

    const abortController = new AbortController()

    props.onCacheStatsRequest(abortController.signal)

    return () => abortController.abort()
  }, [props.onCacheStatsRequest])

  const handleRemoteModelSubmit = React.useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const parsedModelUrl = parseModelUrl(remoteModelUrl, window.location.href)

    if (!parsedModelUrl) {
      setRemoteModelError('Use a direct http(s) link ending in .mdl.')
      return
    }

    setRemoteModelError(null)
    setRemoteModelSubmitting(true)

    try {
      await props.openRemoteModelUrl(parsedModelUrl)
    } catch (error) {
      setRemoteModelError(error instanceof Error ? error.message : 'Could not cache this model.')
    } finally {
      setRemoteModelSubmitting(false)
    }
  }, [props, remoteModelUrl])

  return (
    <Screen>
      <Panel>
        <Eyebrow>Web HLMV</Eyebrow>
        <Title>Open a Half-Life model.</Title>
        <Description>
          Load a `.mdl`, preview animations and inspect body parts in a lighter, more compact interface.
        </Description>

        <RemoteModelForm onSubmit={handleRemoteModelSubmit}>
          <DemoLabel>
            <LabelText>
              Direct .mdl URL
              <Tooltip>
                <TooltipTrigger tabIndex={0} aria-describedby="direct-model-url-tooltip">
                  ?
                </TooltipTrigger>
                <TooltipBubble id="direct-model-url-tooltip" role="tooltip">
                  The server hosting the file must allow requests from any site with
                  the Access-Control-Allow-Origin: * header.
                </TooltipBubble>
              </Tooltip>
            </LabelText>
            <UrlInputRow>
              <UrlInput
                type="url"
                inputMode="url"
                placeholder="https://example.com/models/player.mdl"
                value={remoteModelUrl}
                onChange={event => {
                  setRemoteModelUrl(event.target.value)
                  setRemoteModelError(null)
                }}
                disabled={isRemoteModelSubmitting}
              />
              <ActionButton type="submit" $primary disabled={isRemoteModelSubmitting}>
                {isRemoteModelSubmitting ? 'Caching...' : 'Cache and open'}
              </ActionButton>
            </UrlInputRow>
          </DemoLabel>
        </RemoteModelForm>
        <CacheNotice role="note">
          Direct .mdl URL models are cached for 30 days. Each successful access renews that 30-day window,
          so active shared models stay available.
          <CacheStatsLine aria-live="polite">
            {props.cacheStats
              ? formatCacheStats(props.cacheStats)
              : props.isCacheStatsLoading ? 'Checking cache usage...' : 'Cache usage is unavailable.'}
          </CacheStatsLine>
        </CacheNotice>

        <DemoPicker>
          <DemoLabel>
            Demo model
            <DemoSelect value={selectedDemoModel?.id ?? ''} onChange={event => setSelectedDemoId(event.target.value)}>
              {props.demoModels.map(demoModel => (
                <option key={demoModel.id} value={demoModel.id}>
                  {demoModel.label} - {demoModel.fileName}
                </option>
              ))}
            </DemoSelect>
          </DemoLabel>
        </DemoPicker>

        <Actions>
          <ActionButton
            type="button"
            $primary
            onClick={() => {
              if (props.selectFile) {
                props.selectFile()
              }
            }}
            disabled={!props.selectFile}
          >
            Select file
          </ActionButton>

          <ActionButton
            type="button"
            onClick={() => {
              if (selectedDemoModel) {
                props.openDemoModel(selectedDemoModel)
              }
            }}
            disabled={!selectedDemoModel}
          >
            Open selected demo
          </ActionButton>
        </Actions>

        <Hint>Drag and drop also works anywhere on the page.</Hint>
        {remoteModelError ? <ErrorMessage>{remoteModelError}</ErrorMessage> : null}
        {props.loadError ? <ErrorMessage>{props.loadError}</ErrorMessage> : null}
      </Panel>
    </Screen>
  )
}
